import { isSimpleError, isSimpleErrors, SimpleError } from '@simonbackx/simple-errors';
import { AccessRight } from '@stamhoofd/types/AccessRight';
import { PermissionLevel } from '@stamhoofd/types/PermissionLevel';
import { PermissionsResourceType } from '@stamhoofd/types/PermissionsResourceType';
import { PermissionsResourceKey } from '../PermissionsResourceType.js';
export class EventPermissionChecker {
    /**
     * Will throw error if not allowed to edit/add/delete this event
     * @param event
     * @returns Organization if event for specific organization, else null
     * @throws error if not allowed to write this event
     */
    static async checkEventAccessAsync(event, { getOrganization, userPermissions, userEmail, platform, isPeriodInUse, }) {
        if (!userPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qQ`),
                statusCode: 403,
            });
        }
        if (event.organizationId !== null) {
            let organization;
            try {
                organization = await getOrganization(event.organizationId);
            }
            catch (error) {
                throw new SimpleError({
                    code: 'not_found',
                    message: 'Organization not found',
                    human: $t(`%DY`),
                });
            }
            this.throwIfNoPermissionToWriteEventForOrganization(event, organization, platform, userPermissions, userEmail, isPeriodInUse);
            return organization;
        }
        this.throwIfNoPermissionToWriteNationalOrRegionalEvent(event, userPermissions, userEmail, platform, isPeriodInUse);
        return null;
    }
    /**
     * Will throw error if not allowed to edit/add/delete this event
     * @param event
     * @throws error if not allowed to write this event
     */
    static throwIfNoPermissionToWriteEvent(event, { organization, userPermissions, userEmail, platform, isPeriodInUse, }) {
        if (!userPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qQ`),
                statusCode: 403,
            });
        }
        if (event.organizationId !== null) {
            if (organization === null) {
                throw new Error('Organization not specified.');
            }
            this.throwIfNoPermissionToWriteEventForOrganization(event, organization, platform, userPermissions, userEmail, isPeriodInUse);
        }
        else {
            this.throwIfNoPermissionToWriteNationalOrRegionalEvent(event, userPermissions, userEmail, platform, isPeriodInUse);
        }
    }
    static hasPermissionToWriteEvent(event, options) {
        try {
            this.throwIfNoPermissionToWriteEvent(event, options);
        }
        catch (error) {
            if (isSimpleError(error) || isSimpleErrors(error)) {
                return false;
            }
            throw error;
        }
        return true;
    }
    static throwIfNoPermissionToWriteEventForOrganization(event, organization, platform, userPermissions, userEmail, isPeriodInUse) {
        const accessRight = AccessRight.EventWrite;
        if (event.organizationId !== organization.id) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qR`),
                statusCode: 403,
            });
        }
        const organizationPermissions = userPermissions.forOrganization(organization, platform, userEmail);
        if (!organizationPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qS`),
                statusCode: 403,
            });
        }
        if (this.hasWriteAccessToEventResource(organizationPermissions, event.id, isPeriodInUse)) {
            return;
        }
        if (event.meta.groups === null) {
            if (!organizationPermissions.hasResourceAccessRight(PermissionsResourceType.Groups, PermissionsResourceKey.CurrentPeriod, accessRight)) {
                throw new SimpleError({
                    code: 'permission_denied',
                    message: $t(`%qS`),
                    statusCode: 403,
                });
            }
        }
        else {
            for (const group of event.meta.groups) {
                if (!organizationPermissions.hasResourceAccessRight(PermissionsResourceType.Groups, group.id, accessRight)) {
                    throw new SimpleError({
                        code: 'permission_denied',
                        message: $t(`%qT`),
                        statusCode: 403,
                    });
                }
            }
        }
    }
    static hasWriteAccessToEventResource(permissions, eventId, isPeriodInUse) {
        return permissions.forPeriod(isPeriodInUse)
            .hasResourceAccess(PermissionsResourceType.Events, eventId, PermissionLevel.Write);
    }
    static throwIfNoPermissionToWriteNationalOrRegionalEvent(event, userPermissions, userEmail, platform, isPeriodInUse) {
        const accessRight = AccessRight.EventWrite;
        const platformPermissions = userPermissions.forPlatform(platform, userEmail);
        if (!platformPermissions) {
            throw new SimpleError({
                code: 'permission_denied',
                message: $t(`%qU`),
                statusCode: 403,
            });
        }
        if (this.hasWriteAccessToEventResource(platformPermissions, event.id, isPeriodInUse)) {
            return;
        }
        // organization tags
        if (event.meta.organizationTagIds === null) {
            if (!(platformPermissions.hasResourceAccessRight(PermissionsResourceType.OrganizationTags, PermissionsResourceKey.All, accessRight))) {
                throw new SimpleError({
                    code: 'permission_denied',
                    message: $t(`%qV`),
                    statusCode: 403,
                });
            }
        }
        else {
            for (const tagId of event.meta.organizationTagIds) {
                if (!platformPermissions.hasResourceAccessRight(PermissionsResourceType.OrganizationTags, tagId, accessRight)) {
                    throw new SimpleError({
                        code: 'permission_denied',
                        message: $t(`%qW`),
                        statusCode: 403,
                    });
                }
            }
        }
    }
}
//# sourceMappingURL=EventPermissionChecker.js.map